import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getEmployees, updateEmployee, uploadTaxDocument, getTaxDocuments, deleteTaxDocument } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, Save, FileText, IndianRupee, Building2, 
  GraduationCap, Heart, Home, PiggyBank, Receipt, Info,
  CheckCircle, AlertCircle, Upload, Trash2, Eye, File, X
} from 'lucide-react';
import { toast } from 'sonner';

const DOCUMENT_CATEGORIES = {
  '80c': { label: 'Section 80C', description: 'PPF, ELSS, LIC, NSC, Tax-saving FD, etc.' },
  '80d': { label: 'Section 80D', description: 'Medical Insurance Premium' },
  '80e': { label: 'Section 80E', description: 'Education Loan Interest' },
  '80g': { label: 'Section 80G', description: 'Donations' },
  'hra': { label: 'HRA Exemption', description: 'Rent receipts, Landlord PAN' },
  'home_loan': { label: 'Home Loan', description: 'Home loan interest certificate' },
  'nps': { label: 'NPS 80CCD(1B)', description: 'NPS contribution statement' },
  'other': { label: 'Other', description: 'Other exemptions' }
};

// Document Upload Component - moved outside to avoid nested component definition
const DocumentUploadSection = ({ category, documents, onUpload, onDelete, uploading }) => {
  const categoryDocs = documents.filter(doc => doc.category === category);
  
  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Proof Documents</span>
          {categoryDocs.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {categoryDocs.length} uploaded
            </Badge>
          )}
        </div>
        <label className="cursor-pointer">
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              if (e.target.files[0]) {
                onUpload(category, e.target.files[0]);
                e.target.value = '';
              }
            }}
            disabled={uploading}
          />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            disabled={uploading}
            className="pointer-events-none"
          >
            <Upload size={14} className="mr-1" />
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </label>
      </div>
      
      {categoryDocs.length > 0 && (
        <div className="space-y-2">
          {categoryDocs.map((doc) => (
            <div 
              key={doc.id}
              className="flex items-center justify-between p-2 bg-white rounded border"
            >
              <div className="flex items-center gap-2 min-w-0">
                <File size={14} className="text-indigo-600 flex-shrink-0" />
                <span className="text-sm text-slate-700 truncate">{doc.document_name}</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {doc.document_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => {
                      if (doc.document_url.startsWith('data:')) {
                        const newWindow = window.open();
                        if (doc.document_url.startsWith('data:image')) {
                          newWindow.document.write(`<img src="${doc.document_url}" style="max-width:100%"/>`);
                        } else {
                          newWindow.document.write(`<embed src="${doc.document_url}" width="100%" height="100%" type="application/pdf"/>`);
                        }
                      }
                    }}
                  >
                    <Eye size={14} />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-600 hover:text-red-800"
                  onClick={() => onDelete(doc.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {categoryDocs.length === 0 && (
        <p className="text-xs text-slate-500">No documents uploaded. Upload PDF, JPG, or PNG files (max 5MB).</p>
      )}
    </div>
  );
};

const TaxDeclaration = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [formData, setFormData] = useState({
    tax_regime: 'new',
    investment_80c: 0,
    investment_80d: 0,
    investment_80e: 0,
    investment_80g: 0,
    hra_exemption: 0,
    lta_claimed: 0,
    home_loan_interest: 0,
    nps_contribution: 0,
    other_exemptions: 0
  });

  const currentYear = new Date().getFullYear();
  const financialYear = new Date().getMonth() >= 3 
    ? `${currentYear}-${(currentYear + 1).toString().slice(-2)}`
    : `${currentYear - 1}-${currentYear.toString().slice(-2)}`;

  useEffect(() => {
    fetchEmployeeData();
    fetchDocuments();
  }, [user]);

  const fetchEmployeeData = async () => {
    try {
      const response = await getEmployees();
      const employees = response.data;
      
      // Find current user's employee record
      const currentEmployee = employees.find(e => e.user_id === user?.id);
      
      if (currentEmployee) {
        setEmployee(currentEmployee);
        setFormData({
          tax_regime: currentEmployee.tax_regime || 'new',
          investment_80c: currentEmployee.investment_80c || 0,
          investment_80d: currentEmployee.investment_80d || 0,
          investment_80e: currentEmployee.investment_80e || 0,
          investment_80g: currentEmployee.investment_80g || 0,
          hra_exemption: currentEmployee.hra_exemption || 0,
          lta_claimed: currentEmployee.lta_claimed || 0,
          home_loan_interest: currentEmployee.home_loan_interest || 0,
          nps_contribution: currentEmployee.nps_contribution || 0,
          other_exemptions: currentEmployee.other_exemptions || 0
        });
      }
    } catch (error) {
      toast.error('Failed to fetch employee data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await getTaxDocuments();
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employee) {
      toast.error('Employee record not found');
      return;
    }

    setSaving(true);
    try {
      await updateEmployee(employee.id, {
        ...employee,
        tax_regime: formData.tax_regime,
        investment_80c: parseFloat(formData.investment_80c) || 0,
        investment_80d: parseFloat(formData.investment_80d) || 0,
        investment_80e: parseFloat(formData.investment_80e) || 0,
        investment_80g: parseFloat(formData.investment_80g) || 0,
        hra_exemption: parseFloat(formData.hra_exemption) || 0,
        lta_claimed: parseFloat(formData.lta_claimed) || 0,
        home_loan_interest: parseFloat(formData.home_loan_interest) || 0,
        nps_contribution: parseFloat(formData.nps_contribution) || 0,
        other_exemptions: parseFloat(formData.other_exemptions) || 0
      });
      toast.success('Tax declaration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tax declaration');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (category, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      await uploadTaxDocument(category, file);
      toast.success('Document uploaded successfully');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await deleteTaxDocument(documentId);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Calculate totals
  const totalSection80C = Math.min(parseFloat(formData.investment_80c) || 0, 150000);
  const totalSection80D = Math.min(parseFloat(formData.investment_80d) || 0, 50000);
  const totalSection80E = parseFloat(formData.investment_80e) || 0;
  const totalSection80G = parseFloat(formData.investment_80g) || 0;
  const totalHRA = parseFloat(formData.hra_exemption) || 0;
  const totalHomeLoan = Math.min(parseFloat(formData.home_loan_interest) || 0, 200000);
  const totalNPS = Math.min(parseFloat(formData.nps_contribution) || 0, 50000);
  const totalOther = parseFloat(formData.other_exemptions) || 0;
  
  const totalDeductions = formData.tax_regime === 'old' 
    ? totalSection80C + totalSection80D + totalSection80E + totalSection80G + totalHRA + totalHomeLoan + totalNPS + totalOther
    : 0;

  // Render a document upload section using the extracted component
  const renderDocUpload = (category) => (
    <DocumentUploadSection 
      category={category}
      documents={documents}
      onUpload={handleDocumentUpload}
      onDelete={handleDeleteDocument}
      uploading={uploading}
    />
  );

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle size={48} className="text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Employee Record Not Found</h2>
          <p className="text-slate-500 mt-2">Please contact HR to set up your employee profile.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Tax Declaration</h1>
            <p className="text-slate-600 mt-1">
              Declare your investments for FY {financialYear} to optimize TDS deductions
            </p>
          </div>
          <div className="bg-indigo-50 px-4 py-2 rounded-lg">
            <p className="text-xs text-indigo-600">Financial Year</p>
            <p className="text-lg font-bold text-indigo-800">{financialYear}</p>
          </div>
        </div>

        {/* Tax Regime Selection */}
        <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="text-indigo-600" />
              Select Tax Regime
            </CardTitle>
            <CardDescription>
              Choose between Old and New tax regime. This affects your TDS calculation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div 
                onClick={() => handleChange('tax_regime', 'new')}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.tax_regime === 'new' 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-slate-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">New Tax Regime</h3>
                  {formData.tax_regime === 'new' && (
                    <CheckCircle className="text-indigo-600" size={20} />
                  )}
                </div>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Lower tax rates</li>
                  <li>• Standard deduction of ₹50,000</li>
                  <li>• No investment deductions allowed</li>
                  <li>• Simplified tax calculation</li>
                </ul>
              </div>
              
              <div 
                onClick={() => handleChange('tax_regime', 'old')}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.tax_regime === 'old' 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-slate-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">Old Tax Regime</h3>
                  {formData.tax_regime === 'old' && (
                    <CheckCircle className="text-indigo-600" size={20} />
                  )}
                </div>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Higher tax rates</li>
                  <li>• Section 80C, 80D deductions allowed</li>
                  <li>• HRA exemption available</li>
                  <li>• Home loan interest deduction</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Investment Declarations - Only for Old Regime */}
        {formData.tax_regime === 'old' ? (
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="80c" className="space-y-4">
              <TabsList className="bg-slate-100 p-1">
                <TabsTrigger value="80c" className="flex items-center gap-1">
                  <PiggyBank size={14} />
                  Section 80C
                </TabsTrigger>
                <TabsTrigger value="80d" className="flex items-center gap-1">
                  <Heart size={14} />
                  Section 80D
                </TabsTrigger>
                <TabsTrigger value="housing" className="flex items-center gap-1">
                  <Home size={14} />
                  Housing
                </TabsTrigger>
                <TabsTrigger value="other" className="flex items-center gap-1">
                  <Receipt size={14} />
                  Other
                </TabsTrigger>
              </TabsList>

              {/* Section 80C */}
              <TabsContent value="80c">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PiggyBank className="text-blue-600" />
                      Section 80C - Investments (Max ₹1,50,000)
                    </CardTitle>
                    <CardDescription>
                      PPF, ELSS, Life Insurance Premium, Principal of Home Loan, Tuition Fees, etc.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="investment_80c">Total 80C Investments (₹)</Label>
                      <Input
                        id="investment_80c"
                        type="number"
                        value={formData.investment_80c}
                        onChange={(e) => handleChange('investment_80c', e.target.value)}
                        placeholder="Enter total 80C investments"
                        max={150000}
                      />
                      <p className="text-xs text-slate-500">
                        Includes: PPF, ELSS, LIC Premium, NSC, Tax-saving FD, Children&apos;s tuition fees, etc.
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-800">Declared Amount</span>
                        <span className="font-bold text-blue-800">₹{parseFloat(formData.investment_80c || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm text-blue-800">Eligible Deduction (max ₹1,50,000)</span>
                        <span className="font-bold text-blue-800">₹{totalSection80C.toLocaleString()}</span>
                      </div>
                    </div>

                    {renderDocUpload("80c")}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Section 80D */}
              <TabsContent value="80d">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="text-red-600" />
                      Section 80D - Medical Insurance (Max ₹50,000)
                    </CardTitle>
                    <CardDescription>
                      Health insurance premium for self, spouse, children, and parents
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="investment_80d">Medical Insurance Premium (₹)</Label>
                      <Input
                        id="investment_80d"
                        type="number"
                        value={formData.investment_80d}
                        onChange={(e) => handleChange('investment_80d', e.target.value)}
                        placeholder="Enter medical insurance premium"
                        max={50000}
                      />
                      <p className="text-xs text-slate-500">
                        Self/Family: up to ₹25,000 | Parents (senior): additional ₹50,000
                      </p>
                    </div>
                    
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-red-800">Eligible Deduction</span>
                        <span className="font-bold text-red-800">₹{totalSection80D.toLocaleString()}</span>
                      </div>
                    </div>

                    {renderDocUpload("80d")}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Housing */}
              <TabsContent value="housing">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="text-emerald-600" />
                      Housing Benefits
                    </CardTitle>
                    <CardDescription>
                      HRA exemption and Home Loan interest deduction
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="hra_exemption">HRA Exemption Claimed (₹)</Label>
                      <Input
                        id="hra_exemption"
                        type="number"
                        value={formData.hra_exemption}
                        onChange={(e) => handleChange('hra_exemption', e.target.value)}
                        placeholder="Enter HRA exemption amount"
                      />
                      <p className="text-xs text-slate-500">
                        Minimum of: Actual HRA received, Rent paid - 10% of salary, 50%/40% of salary
                      </p>
                      {renderDocUpload("hra")}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="home_loan_interest">Home Loan Interest - Section 24(b) (₹)</Label>
                      <Input
                        id="home_loan_interest"
                        type="number"
                        value={formData.home_loan_interest}
                        onChange={(e) => handleChange('home_loan_interest', e.target.value)}
                        placeholder="Enter home loan interest"
                        max={200000}
                      />
                      <p className="text-xs text-slate-500">
                        Maximum deduction: ₹2,00,000 for self-occupied property
                      </p>
                      {renderDocUpload("home_loan")}
                    </div>
                    
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-emerald-800">Total Housing Benefits</span>
                        <span className="font-bold text-emerald-800">₹{(totalHRA + totalHomeLoan).toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Other Deductions */}
              <TabsContent value="other">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="text-purple-600" />
                      Other Deductions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="investment_80e">
                          <GraduationCap className="inline mr-1" size={14} />
                          Section 80E - Education Loan Interest (₹)
                        </Label>
                        <Input
                          id="investment_80e"
                          type="number"
                          value={formData.investment_80e}
                          onChange={(e) => handleChange('investment_80e', e.target.value)}
                          placeholder="Enter education loan interest"
                        />
                        <p className="text-xs text-slate-500">No upper limit</p>
                        {renderDocUpload("80e")}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="investment_80g">
                          <Heart className="inline mr-1" size={14} />
                          Section 80G - Donations (₹)
                        </Label>
                        <Input
                          id="investment_80g"
                          type="number"
                          value={formData.investment_80g}
                          onChange={(e) => handleChange('investment_80g', e.target.value)}
                          placeholder="Enter donations"
                        />
                        <p className="text-xs text-slate-500">50% or 100% deduction based on fund</p>
                        {renderDocUpload("80g")}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="nps_contribution">
                          <PiggyBank className="inline mr-1" size={14} />
                          Section 80CCD(1B) - NPS Contribution (₹)
                        </Label>
                        <Input
                          id="nps_contribution"
                          type="number"
                          value={formData.nps_contribution}
                          onChange={(e) => handleChange('nps_contribution', e.target.value)}
                          placeholder="Enter NPS contribution"
                          max={50000}
                        />
                        <p className="text-xs text-slate-500">Additional ₹50,000 over 80C limit</p>
                        {renderDocUpload("nps")}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="other_exemptions">
                          <Receipt className="inline mr-1" size={14} />
                          Other Exemptions (₹)
                        </Label>
                        <Input
                          id="other_exemptions"
                          type="number"
                          value={formData.other_exemptions}
                          onChange={(e) => handleChange('other_exemptions', e.target.value)}
                          placeholder="Enter other exemptions"
                        />
                        <p className="text-xs text-slate-500">LTA, other allowances, etc.</p>
                        {renderDocUpload("other")}
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-purple-800">Total Other Deductions</span>
                        <span className="font-bold text-purple-800">
                          ₹{(totalSection80E + totalSection80G + totalNPS + totalOther).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Summary Card */}
            <Card className="mt-6 border-2 border-emerald-200 bg-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <Calculator />
                  Declaration Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-slate-500">Section 80C</p>
                    <p className="text-lg font-bold text-blue-600">₹{totalSection80C.toLocaleString()}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-slate-500">Section 80D</p>
                    <p className="text-lg font-bold text-red-600">₹{totalSection80D.toLocaleString()}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-slate-500">Housing</p>
                    <p className="text-lg font-bold text-emerald-600">₹{(totalHRA + totalHomeLoan).toLocaleString()}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-slate-500">Other</p>
                    <p className="text-lg font-bold text-purple-600">₹{(totalSection80E + totalSection80G + totalNPS + totalOther).toLocaleString()}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-emerald-200 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-emerald-800">Total Eligible Deductions</p>
                    <p className="text-2xl font-bold text-emerald-800">₹{totalDeductions.toLocaleString()}</p>
                  </div>
                  <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                    <Save className="mr-2" size={18} />
                    {saving ? 'Saving...' : 'Save Declaration'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        ) : (
          /* New Regime - No declarations needed */
          <Card className="border border-slate-200">
            <CardContent className="p-8 text-center">
              <Info size={48} className="mx-auto text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 mb-2">New Tax Regime Selected</h3>
              <p className="text-slate-600 max-w-lg mx-auto">
                Under the new tax regime, you cannot claim deductions under Section 80C, 80D, HRA, etc.
                However, you benefit from lower tax rates and a standard deduction of ₹50,000.
              </p>
              <div className="mt-6">
                <Button onClick={handleSubmit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save className="mr-2" size={18} />
                  {saving ? 'Saving...' : 'Confirm Tax Regime'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Information Box */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 mt-0.5" size={20} />
              <div>
                <h4 className="font-medium text-amber-800">Important Notes</h4>
                <ul className="text-sm text-amber-700 mt-1 space-y-1">
                  <li>• Upload proof documents for each investment category to avoid TDS adjustments</li>
                  <li>• Submit investment proofs to HR by the deadline (usually January-February)</li>
                  <li>• Declarations without proof may result in TDS adjustment in later months</li>
                  <li>• You can change tax regime at the time of filing ITR if beneficial</li>
                  <li>• Contact HR for any questions regarding tax calculations</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TaxDeclaration;
